<template>
  <div>
    <div class="">
      <div v-if="inTextMode">
        {{ contentForTextMode }}
        <span v-if="isAvailable">
          <i
            v-tooltip:top="
              $t('Available!') +
              '<br><small>' +
              $t('The freelancer has recently told us, that they’re available') +
              '</small>'
            "
            class="zmdi zmdi-calendar-check iblock c-black m-l-5 f-18"
          />
        </span>
        <span v-else-if="isConfidentInAvailability">
          <i
            v-tooltip:top="$t('The availability of this freelancer has recently been updated.')"
            class="zmdi zmdi-calendar-check iblock c-gray m-l-5 f-18"
          />
        </span>
        <span v-else>
          <i
            v-tooltip:top="
              $t(
                'This data is a little dated. We recommend that you reach out to the freelancer for their current availability!',
              )
            "
            class="zmdi zmdi-hourglass iblock m-l-5 f-18 c-gray"
          />
        </span>
      </div>
      <div v-if="!inTextMode">
        <div
          v-for="availabilityOption in availabilityOptions"
          :key="availabilityOption.value"
          class="row"
        >
          <div class="col-sm-10">
            <div class="radio">
              <label>
                <input
                  v-model="availability"
                  :value="availabilityOption.value"
                  name="job_ready"
                  type="radio"
                  @change="handleSelectionChange"
                />
                <i class="input-helper" />
                {{ $t(availabilityOption.label) }}
                &nbsp;
                <span class="c-gray">{{ availabilityOption.helperText }}</span>
              </label>
            </div>
          </div>
          <div class="col-sm-2 m-h-20" style="margin-top: -15px">
            <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
            <NetworkLoader
              :loader="saving && availability === availabilityOption.value"
              :failed="saveFailed && availability === availabilityOption.value"
            />
          </div>
        </div>
      </div>
      <div v-show="showDateSelection" id="jobdate-start" class="row">
        <div class="m-l-35 col-sm-6">
          <PickerDatetime
            v-model="availableDate"
            as-input
            float-label
            name="job_ready_date"
            :placeholder="$t('Choose here')"
            @input="handleSelectionChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import enums from '@/utils/enum'
import { SET_EDIT_UNSAVED, UPDATE_USER_STATE } from '@/state/mutation-types'
import { SET_EDIT_SAVING_STATE } from '@/state/action-types'
import PickerDatetime from '@/components/PickerDatetime.vue'
import { UPDATE_FREELANCER } from '@/state/apollo/accounts'
import { NetworkError, Notification } from '@/utils/notification'

/**
 * note: due to strange behavior in jquery datepicker events, there is some
 * tricky logic with the order of attaching listeners to the event
 */
export default {
  components: { PickerDatetime },
  props: {
    isMe: { type: Boolean, required: true },
    showInputOnly: { type: Boolean, default: false },
    initialAvailability: { type: String, default: '' },
    initialAvailableDate: { type: String, default: '' },
    isConfidentInAvailability: { type: Boolean, default: false },
    isAvailable: { type: Boolean, default: false },
  },
  data() {
    return {
      saving: false,
      saveFailed: false,
      availabilityOptions: [
        { value: 'rightaway', label: 'Immediately', helperText: '' },
        {
          value: '14days',
          label: '14 days',
          helperText: $t('(Eg. around :date from now)', {
            date: moment().add(2, 'weeks').format('MMM D'),
          }),
        },
        {
          value: 'nextmonth',
          label: 'One month ahead',
          helperText: $t('(Eg. :date from now)', {
            date: moment().add(45, 'days').format('MMM 1'),
          }),
        },
        {
          value: '3months',
          label: '3 months ahead',
          helperText: $t('(Eg. :date from now)', {
            date: moment().add(105, 'days').format('MMM 1'),
          }),
        },
        {
          value: 'ondate',
          label: "I'm available on a specific date:",
          helperText: '',
        },
      ],
      availability: '',
      availableDate: '',
      calendarHandlerAdded: false,
    }
  },
  computed: {
    inTextMode() {
      return !this.showInputOnly && (!this.isMe || !this.$store.state.edit.on)
    },
    contentForTextMode() {
      if (this.availability === 'ondate') {
        return $t('Available from :date', { date: this.availableDate })
      }
      return $t('Typically available :date', {
        date: $t(`job_ready.${this.availability}`).toLowerCase(),
      })
    },
    showDateSelection() {
      return this.availability === 'ondate' && !this.inTextMode
    },
  },
  created() {
    this.availability = this.initialAvailability
    this.availableDate = this.initialAvailableDate
  },
  methods: {
    handleSelectionChange() {
      this.$emit('user-interacted')
      this.saveChanges()
    },
    // Note: this method is also called from a $ref in modal-for-account-settings
    async saveChanges() {
      this.saveFailed = false
      this.saving = true
      this.$emit('saving')
      this.$store.dispatch(SET_EDIT_SAVING_STATE, true)
      this.$store.commit(SET_EDIT_UNSAVED, true)
      try {
        const input = {
          globalId: this.account.globalId,
          jobReady: this.availability,
          jobReadyDate: this.availableDate,
          meta: {
            last_updated_availability: moment().format('YYYY-MM-DD HH:mm:ss'),
          },
        }

        // Remove as only needed when ondate type
        if (this.availability !== 'ondate') {
          delete input.jobReadyDate
        }

        await this.$apollo.mutate({
          mutation: UPDATE_FREELANCER,
          variables: {
            input,
          },
        })
        this.$store.commit(UPDATE_USER_STATE, {
          fieldName: 'job_ready',
          fieldValue: this.availability,
        })
        this.$store.commit(UPDATE_USER_STATE, {
          fieldName: 'job_ready_date',
          fieldValue: this.availableDate,
        })
        this.$store.dispatch(SET_EDIT_SAVING_STATE, false)
        this.$store.commit(SET_EDIT_UNSAVED, false)
      } catch (e) {
        this.saveFailed = true
        this.$emit('done-saving')
        if (e.graphQLErrors.length > 0) {
          new Notification({
            msg: e.graphQLErrors[0].message,
            type: enums.notification.types.ERROR,
            severity: enums.notification.severity.CRITICAL,
            config: { timeout: 5000 },
          }).dispatch(this)
        } else {
          new NetworkError({
            severity: enums.notification.severity.CRITICAL,
            ex: e,
          }).dispatch(this)
        }
      }
      this.saving = false
      this.$emit('done-saving')
      this.$store.dispatch(SET_EDIT_SAVING_STATE, false)
    },
  },
}
</script>
